import React, { forwardRef } from 'react';
import cn from 'classnames';

type SpacingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type BorderRadiusSize = 'none' | 'sm' | 'md' | 'lg' | 'full';

interface BorderRadiusShorthand {
  rounded?: BorderRadiusSize;
  tl?: never;
  tr?: never;
  br?: never;
  bl?: never;
}

interface BorderRadiusExplicit {
  rounded?: never;
  tl?: BorderRadiusSize;
  tr?: BorderRadiusSize;
  br?: BorderRadiusSize;
  bl?: BorderRadiusSize;
}

type BorderRadius = BorderRadiusShorthand | BorderRadiusExplicit;

interface PaddingShorthand {
  p?: SpacingSize;
  px?: never;
  py?: never;
  pt?: never;
  pr?: never;
  pb?: never;
  pl?: never;
}

interface PaddingExplicit {
  p?: never;
  px?: SpacingSize | `px-[${string}]`;
  py?: SpacingSize | `py-[${string}]`;
  pt?: SpacingSize | `pt-[${string}]`;
  pr?: SpacingSize | `pr-[${string}]`;
  pb?: SpacingSize | `pb-[${string}]`;
  pl?: SpacingSize | `pl-[${string}]`;
}

type Padding = PaddingShorthand | PaddingExplicit;

interface MarginShorthand {
  m?: SpacingSize;
  mx?: never;
  my?: never;
  mt?: never;
  mr?: never;
  mb?: never;
  ml?: never;
}

interface MarginExplicit {
  m?: never;
  mx?: SpacingSize | `mx-[${string}]`;
  my?: SpacingSize | `my-[${string}]`;
  mt?: SpacingSize | `mt-[${string}]`;
  mr?: SpacingSize | `mr-[${string}]`;
  mb?: SpacingSize | `mb-[${string}]`;
  ml?: SpacingSize | `ml-[${string}]`;
}

type Margin = MarginShorthand | MarginExplicit;

export type BoxSpacingProps = Padding & Margin;

const borderRadiusToClassName: Record<
  keyof Required<BorderRadius>,
  Record<BorderRadiusSize, string>
> = {
  rounded: {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  },
  tl: {
    none: 'rounded-tl-none',
    sm: 'rounded-tl-sm',
    md: 'rounded-tl-md',
    lg: 'rounded-tl-lg',
    full: 'rounded-tl-full',
  },
  tr: {
    none: 'rounded-tr-none',
    sm: 'rounded-tr-sm',
    md: 'rounded-tr-md',
    lg: 'rounded-tr-lg',
    full: 'rounded-tr-full',
  },
  br: {
    none: 'rounded-br-none',
    sm: 'rounded-br-sm',
    md: 'rounded-br-md',
    lg: 'rounded-br-lg',
    full: 'rounded-br-full',
  },
  bl: {
    none: 'rounded-bl-none',
    sm: 'rounded-bl-sm',
    md: 'rounded-bl-md',
    lg: 'rounded-bl-lg',
    full: 'rounded-bl-full',
  },
};

const boxSpacingToClassName: Record<
  keyof Required<BoxSpacingProps>,
  Record<SpacingSize, string>
> = {
  p: {
    xs: 'p-2',
    sm: 'p-4',
    md: 'p-8',
    lg: 'p-16',
    xl: 'p-24',
  },
  px: {
    xs: 'px-2',
    sm: 'px-4',
    md: 'px-8',
    lg: 'px-16',
    xl: 'px-24',
  },
  py: {
    xs: 'py-2',
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-16',
    xl: 'py-24',
  },
  pt: {
    xs: 'pt-2',
    sm: 'pt-4',
    md: 'pt-8',
    lg: 'pt-16',
    xl: 'pt-24',
  },
  pr: {
    xs: 'pr-2',
    sm: 'pr-4',
    md: 'pr-8',
    lg: 'pr-16',
    xl: 'pr-24',
  },
  pb: {
    xs: 'pb-2',
    sm: 'pb-4',
    md: 'pb-8',
    lg: 'pb-16',
    xl: 'pb-24',
  },
  pl: {
    xs: 'pl-2',
    sm: 'pl-4',
    md: 'pl-8',
    lg: 'pl-16',
    xl: 'pl-24',
  },
  m: {
    xs: 'm-2',
    sm: 'm-4',
    md: 'm-8',
    lg: 'm-16',
    xl: 'm-24',
  },
  mx: {
    xs: 'mx-2',
    sm: 'mx-4',
    md: 'mx-8',
    lg: 'mx-16',
    xl: 'mx-24',
  },
  my: {
    xs: 'my-2',
    sm: 'my-4',
    md: 'my-8',
    lg: 'my-16',
    xl: 'my-24',
  },
  mt: {
    xs: 'mt-2',
    sm: 'mt-4',
    md: 'mt-8',
    lg: 'mt-16',
    xl: 'mt-24',
  },
  mr: {
    xs: 'mr-2',
    sm: 'mr-4',
    md: 'mr-8',
    lg: 'mr-16',
    xl: 'mr-24',
  },
  mb: {
    xs: 'mb-2',
    sm: 'mb-4',
    md: 'mb-8',
    lg: 'mb-16',
    xl: 'mb-24',
  },
  ml: {
    xs: 'ml-2',
    sm: 'ml-4',
    md: 'ml-8',
    lg: 'ml-16',
    xl: 'ml-24',
  },
};

type OverflowStyle = 'auto' | 'hidden' | 'visible' | 'scroll';

export type BoxOverflowProps =
  | {
      /**
       * You can only use overflow or (overflowX and/or overflowY)
       */
      overflow?: OverflowStyle;
      overflowX?: never;
      overflowY?: never;
    }
  | {
      overflow?: never;
      /**
       * You can only use overflow or (overflowX and/or overflowY)
       */
      overflowX?: OverflowStyle;
      /**
       * You can only use overflow or (overflowX and/or overflowY)
       */
      overflowY?: OverflowStyle;
    };

const boxOverflowToClassName: Record<
  keyof Required<BoxOverflowProps>,
  Record<OverflowStyle, string>
> = {
  overflow: {
    auto: 'overflow-auto',
    hidden: 'overflow-hidden',
    visible: 'overflow-visible',
    scroll: 'overflow-scroll',
  },
  overflowX: {
    auto: 'overflow-x-auto',
    hidden: 'overflow-x-hidden',
    visible: 'overflow-x-visible',
    scroll: 'overflow-x-scroll',
  },
  overflowY: {
    auto: 'overflow-y-auto',
    hidden: 'overflow-y-hidden',
    visible: 'overflow-y-visible',
    scroll: 'overflow-y-scroll',
  },
};

type MinWidthBoxSize =
  | '0'
  | '0.5'
  | '1'
  | '1.5'
  | '2'
  | '2.5'
  | '3'
  | '3.5'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '14'
  | '16'
  | '20'
  | '24'
  | '28'
  | '32'
  | '36'
  | '40'
  | '44'
  | '47'
  | '48'
  | '52'
  | '56'
  | '60'
  | '64'
  | '72'
  | '80'
  | '96'
  | 'px'
  | 'full'
  | 'min'
  | 'max'
  | 'fit';

type MinHeightBoxSize = MinWidthBoxSize | 'screen';

type HeightBoxSize =
  | MinHeightBoxSize
  | 'auto'
  | 'screen'
  | '1/2'
  | '1/3'
  | '2/3'
  | '1/4'
  | '2/4'
  | '3/4'
  | '1/6'
  | '2/6'
  | '3/6'
  | '4/6'
  | '5/6';

type WidthBoxSize =
  | HeightBoxSize
  | '1/12'
  | '2/12'
  | '3/12'
  | '4/12'
  | '5/12'
  | '6/12'
  | '7/12'
  | '8/12'
  | '9/12'
  | '10/12'
  | '11/12';

type MaxWidthBoxSize =
  | MinWidthBoxSize
  | 'none'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'prose'
  | 'screen-sm'
  | 'screen-md'
  | 'screen-lg'
  | 'screen-xl'
  | 'screen-2xl';

type MaxHeightBoxSize = MinHeightBoxSize;

export type BoxLayoutProps = {
  /**
   * Allowing custom values like `w-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  w?: WidthBoxSize | `w-[${string}]`;
  /**
   * Allowing custom values like `h-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  h?: HeightBoxSize | `h-[${string}]`;
  /**
   * Allowing custom values like `min-h-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  minH?: MinHeightBoxSize | `min-h-[${string}]`;
  /**
   * Allowing custom values like `min-w-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  minW?: MinWidthBoxSize | `min-w-[${string}]`;
  /**
   * Allowing custom values like `max-h-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  maxH?: MaxHeightBoxSize | `max-h-[${string}]`;
  /**
   * Allowing custom values like `max-w-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  maxW?: MaxWidthBoxSize | `max-w-[${string}]`;

  /**
   * Aligns the box along the cross axis
   */
  alignSelf?: 'start' | 'center' | 'end';
};

const boxSizeToClassName: {
  w: Record<WidthBoxSize, string>;
  h: Record<HeightBoxSize, string>;
  minW: Record<MinWidthBoxSize, string>;
  minH: Record<MinHeightBoxSize, string>;
  maxW: Record<MaxWidthBoxSize, string>;
  maxH: Record<MaxHeightBoxSize, string>;
} = {
  w: {
    px: 'w-0',
    '0': 'w-0',
    '0.5': 'w-0.5',
    '1': 'w-1',
    '1.5': 'w-1.5',
    '2': 'w-2',
    '2.5': 'w-2.5',
    '3': 'w-3',
    '3.5': 'w-3.5',
    '4': 'w-4',
    '5': 'w-5',
    '6': 'w-6',
    '7': 'w-7',
    '8': 'w-8',
    '9': 'w-9',
    '10': 'w-10',
    '11': 'w-11',
    '12': 'w-12',
    '14': 'w-14',
    '16': 'w-16',
    '20': 'w-20',
    '24': 'w-24',
    '28': 'w-28',
    '32': 'w-32',
    '36': 'w-36',
    '40': 'w-40',
    '44': 'w-44',
    '47': 'w-47',
    '48': 'w-48',
    '52': 'w-52',
    '56': 'w-56',
    '60': 'w-60',
    '64': 'w-64',
    '72': 'w-72',
    '80': 'w-80',
    '96': 'w-96',
    auto: 'w-auto',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '2/3': 'w-2/3',
    '1/4': 'w-1/4',
    '2/4': 'w-2/4',
    '3/4': 'w-3/4',
    '1/6': 'w-1/6',
    '2/6': 'w-2/6',
    '3/6': 'w-3/6',
    '4/6': 'w-4/6',
    '5/6': 'w-5/6',
    '1/12': 'w-1/12',
    '2/12': 'w-2/12',
    '3/12': 'w-3/12',
    '4/12': 'w-4/12',
    '5/12': 'w-5/12',
    '6/12': 'w-6/12',
    '7/12': 'w-7/12',
    '8/12': 'w-8/12',
    '9/12': 'w-9/12',
    '10/12': 'w-10/12',
    '11/12': 'w-11/12',
    screen: 'w-screen',
    full: 'w-full',
    min: 'w-min',
    max: 'w-max',
    fit: 'w-fit',
  },
  h: {
    px: 'h-px',
    '0': 'h-0',
    '0.5': 'h-0.5',
    '1': 'h-1',
    '1.5': 'h-1.5',
    '2': 'h-2',
    '2.5': 'h-2.5',
    '3': 'h-3',
    '3.5': 'h-3.5',
    '4': 'h-4',
    '5': 'h-5',
    '6': 'h-6',
    '7': 'h-7',
    '8': 'h-8',
    '9': 'h-9',
    '10': 'h-10',
    '11': 'h-11',
    '12': 'h-12',
    '14': 'h-14',
    '16': 'h-16',
    '20': 'h-20',
    '24': 'h-24',
    '28': 'h-28',
    '32': 'h-32',
    '36': 'h-36',
    '40': 'h-40',
    '44': 'h-44',
    '47': 'h-47',
    '48': 'h-48',
    '52': 'h-52',
    '56': 'h-56',
    '60': 'h-60',
    '64': 'h-64',
    '72': 'h-72',
    '80': 'h-80',
    '96': 'h-96',
    auto: 'h-auto',
    '1/2': 'h-1/2',
    '1/3': 'h-1/3',
    '2/3': 'h-2/3',
    '1/4': 'h-1/4',
    '2/4': 'h-2/4',
    '3/4': 'h-3/4',
    '1/6': 'h-1/6',
    '2/6': 'h-2/6',
    '3/6': 'h-3/6',
    '4/6': 'h-4/6',
    '5/6': 'h-5/6',
    screen: 'h-screen',
    full: 'h-full',
    min: 'h-min',
    max: 'h-max',
    fit: 'h-fit',
  },
  minW: {
    px: 'min-w-px',
    '0': 'min-w-0',
    '0.5': 'min-w-0.5',
    '1': 'min-w-1',
    '1.5': 'min-w-1.5',
    '2': 'min-w-2',
    '2.5': 'min-w-2.5',
    '3': 'min-w-3',
    '3.5': 'min-w-3.5',
    '4': 'min-w-4',
    '5': 'min-w-5',
    '6': 'min-w-6',
    '7': 'min-w-7',
    '8': 'min-w-8',
    '9': 'min-w-9',
    '10': 'min-w-10',
    '11': 'min-w-11',
    '12': 'min-w-12',
    '14': 'min-w-14',
    '16': 'min-w-16',
    '20': 'min-w-20',
    '24': 'min-w-24',
    '28': 'min-w-28',
    '32': 'min-w-32',
    '36': 'min-w-36',
    '40': 'min-w-40',
    '44': 'min-w-44',
    '47': 'min-w-47',
    '48': 'min-w-48',
    '52': 'min-w-52',
    '56': 'min-w-56',
    '60': 'min-w-60',
    '64': 'min-w-64',
    '72': 'min-w-72',
    '80': 'min-w-80',
    '96': 'min-w-96',
    full: 'min-w-full',
    min: 'min-w-min',
    max: 'min-w-max',
    fit: 'min-w-fit',
  },
  minH: {
    px: 'min-h-px',
    '0': 'min-h-0',
    '0.5': 'min-h-0.5',
    '1': 'min-h-1',
    '1.5': 'min-h-1.5',
    '2': 'min-h-2',
    '2.5': 'min-h-2.5',
    '3': 'min-h-3',
    '3.5': 'min-h-3.5',
    '4': 'min-h-4',
    '5': 'min-h-5',
    '6': 'min-h-6',
    '7': 'min-h-7',
    '8': 'min-h-8',
    '9': 'min-h-9',
    '10': 'min-h-10',
    '11': 'min-h-11',
    '12': 'min-h-12',
    '14': 'min-h-14',
    '16': 'min-h-16',
    '20': 'min-h-20',
    '24': 'min-h-24',
    '28': 'min-h-28',
    '32': 'min-h-32',
    '36': 'min-h-36',
    '40': 'min-h-40',
    '44': 'min-h-44',
    '47': 'min-h-47',
    '48': 'min-h-48',
    '52': 'min-h-52',
    '56': 'min-h-56',
    '60': 'min-h-60',
    '64': 'min-h-64',
    '72': 'min-h-72',
    '80': 'min-h-80',
    '96': 'min-h-96',
    screen: 'min-h-screen',
    full: 'min-h-full',
    min: 'min-h-min',
    max: 'min-h-max',
    fit: 'min-h-fit',
  },
  maxW: {
    none: 'max-w-none',
    px: 'max-w-px',
    '0': 'max-w-0',
    '0.5': 'max-w-0.5',
    '1': 'max-w-1',
    '1.5': 'max-w-1.5',
    '2': 'max-w-2',
    '2.5': 'max-w-2.5',
    '3': 'max-w-3',
    '3.5': 'max-w-3.5',
    '4': 'max-w-4',
    '5': 'max-w-5',
    '6': 'max-w-6',
    '7': 'max-w-7',
    '8': 'max-w-8',
    '9': 'max-w-9',
    '10': 'max-w-10',
    '11': 'max-w-11',
    '12': 'max-w-12',
    '14': 'max-w-14',
    '16': 'max-w-16',
    '20': 'max-w-20',
    '24': 'max-w-24',
    '28': 'max-w-28',
    '32': 'max-w-32',
    '36': 'max-w-36',
    '40': 'max-w-40',
    '44': 'max-w-44',
    '47': 'max-w-47',
    '48': 'max-w-48',
    '52': 'max-w-52',
    '56': 'max-w-56',
    '60': 'max-w-60',
    '64': 'max-w-64',
    '72': 'max-w-72',
    '80': 'max-w-80',
    '96': 'max-w-96',
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    'screen-sm': 'max-w-screen-sm',
    'screen-md': 'max-w-screen-md',
    'screen-lg': 'max-w-screen-lg',
    'screen-xl': 'max-w-screen-xl',
    'screen-2xl': 'max-w-screen-2xl',
    prose: 'max-w-prose',
    full: 'max-w-full',
    min: 'max-w-min',
    max: 'max-w-max',
    fit: 'max-w-fit',
  },
  maxH: {
    px: 'max-h-px',
    '0': 'max-h-0',
    '0.5': 'max-h-0.5',
    '1': 'max-h-1',
    '1.5': 'max-h-1.5',
    '2': 'max-h-2',
    '2.5': 'max-h-2.5',
    '3': 'max-h-3',
    '3.5': 'max-h-3.5',
    '4': 'max-h-4',
    '5': 'max-h-5',
    '6': 'max-h-6',
    '7': 'max-h-7',
    '8': 'max-h-8',
    '9': 'max-h-9',
    '10': 'max-h-10',
    '11': 'max-h-11',
    '12': 'max-h-12',
    '14': 'max-h-14',
    '16': 'max-h-16',
    '20': 'max-h-20',
    '24': 'max-h-24',
    '28': 'max-h-28',
    '32': 'max-h-32',
    '36': 'max-h-36',
    '40': 'max-h-40',
    '44': 'max-h-44',
    '47': 'max-h-47',
    '48': 'max-h-48',
    '52': 'max-h-52',
    '56': 'max-h-56',
    '60': 'max-h-60',
    '64': 'max-h-64',
    '72': 'max-h-72',
    '80': 'max-h-80',
    '96': 'max-h-96',
    screen: 'max-h-screen',
    full: 'max-h-full',
    min: 'max-h-min',
    max: 'max-h-max',
    fit: 'max-h-fit',
  },
};

/**
 * Returns true if the value is a tailwindcss size override like `w-[100px]`
 */
const isCustomSize = (value: string): boolean =>
  value.includes('[') && value.includes(']');

const boxLayoutToClassName = (
  prop: keyof BoxLayoutProps,
  value: string,
): string => {
  switch (prop) {
    case 'w':
      return boxSizeToClassName.w[value as WidthBoxSize] ?? '';
    case 'h':
      return boxSizeToClassName.h[value as HeightBoxSize] ?? '';
    case 'minW':
      return boxSizeToClassName.minW[value as MinWidthBoxSize] ?? '';
    case 'minH':
      return boxSizeToClassName.minH[value as MinHeightBoxSize] ?? '';
    case 'maxW':
      return boxSizeToClassName.maxW[value as MaxWidthBoxSize] ?? '';
    case 'maxH':
      return boxSizeToClassName.maxH[value as MaxHeightBoxSize] ?? '';
    default:
      return '';
  }
};

/**
 * @todo add more values if needed. Tailwind supports a lot
 * @url https://tailwindcss.com/docs/top-right-bottom-left
 */
type BoxPositionSize =
  | '0'
  | '0.5'
  | '1'
  | '1.5'
  | '2'
  | '2.5'
  | '3'
  | '3.5'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '1/2'
  | '-1/2'
  | 'auto';

export type BoxBorderProps = BorderRadius;

export interface BoxPositionProps {
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky' | 'static';
  /**
   * Allowing custom values like `top-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  top?: BoxPositionSize | `top-[${string}]`;
  /**
   * Allowing custom values like `right-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  right?: BoxPositionSize | `right-[${string}]`;
  /**
   * Allowing custom values like `bottom-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  bottom?: BoxPositionSize | `bottom-[${string}]`;
  /**
   * Allowing custom values like `left-[100px]` since we can't predict all use cases with a fixed set of values
   *
   * Use with caution
   */
  left?: BoxPositionSize | `left-[${string}]`;
  zIndex?: '0' | '10' | '20' | '30' | '40' | '50' | 'auto';
}

const boxPositionToClassNameMap: Record<
  keyof BoxPositionProps,
  Record<string, string>
> = {
  position: {
    relative: 'relative',
    absolute: 'absolute',
    fixed: 'fixed',
    sticky: 'sticky',
    static: 'static',
  },
  top: {
    '0': 'top-0',
    '0.5': 'top-0.5',
    '1': 'top-1',
    '1.5': 'top-1.5',
    '2': 'top-2',
    '2.5': 'top-2.5',
    '3': 'top-3',
    '3.5': 'top-3.5',
    '4': 'top-4',
    '5': 'top-5',
    '6': 'top-6',
    '7': 'top-7',
    '8': 'top-8',
    '9': 'top-9',
    '10': 'top-10',
    '1/2': 'top-1/2',
    '-1/2': '-top-1/2',
    auto: 'top-auto',
  },
  right: {
    '0': 'right-0',
    '0.5': 'right-0.5',
    '1': 'right-1',
    '1.5': 'right-1.5',
    '2': 'right-2',
    '2.5': 'right-2.5',
    '3': 'right-3',
    '3.5': 'right-3.5',
    '4': 'right-4',
    '5': 'right-5',
    '6': 'right-6',
    '7': 'right-7',
    '8': 'right-8',
    '9': 'right-9',
    '10': 'right-10',
    '1/2': 'right-1/2',
    '-1/2': '-right-1/2',
    auto: 'right-auto',
  },
  bottom: {
    '0': 'bottom-0',
    '0.5': 'bottom-0.5',
    '1': 'bottom-1',
    '1.5': 'bottom-1.5',
    '2': 'bottom-2',
    '2.5': 'bottom-2.5',
    '3': 'bottom-3',
    '3.5': 'bottom-3.5',
    '4': 'bottom-4',
    '5': 'bottom-5',
    '6': 'bottom-6',
    '7': 'bottom-7',
    '8': 'bottom-8',
    '9': 'bottom-9',
    '10': 'bottom-10',
    '1/2': 'bottom-1/2',
    '-1/2': '-bottom-1/2',
    auto: 'bottom-auto',
  },
  left: {
    '0': 'left-0',
    '0.5': 'left-0.5',
    '1': 'left-1',
    '1.5': 'left-1.5',
    '2': 'left-2',
    '2.5': 'left-2.5',
    '3': 'left-3',
    '3.5': 'left-3.5',
    '4': 'left-4',
    '5': 'left-5',
    '6': 'left-6',
    '7': 'left-7',
    '8': 'left-8',
    '9': 'left-9',
    '10': 'left-10',
    '1/2': 'left-1/2',
    '-1/2': '-left-1/2',
    auto: 'left-auto',
  },
  zIndex: {
    '0': 'z-0',
    '10': 'z-10',
    '20': 'z-20',
    '30': 'z-30',
    '40': 'z-40',
    '50': 'z-50',
    auto: 'z-auto',
  },
};

const boxPositionToClassName = (
  prop: keyof BoxPositionProps,
  value: string,
): string => {
  return boxPositionToClassNameMap[prop][value] ?? '';
};

interface BoxColorProps {
  /** @todo add more colors */
  bg?:
    | 'white'
    | 'transparent'
    | 'gray-100'
    | 'gray-200'
    | 'gray-300'
    | 'red-100'
    | 'green-100'
    | 'green-300'
    | 'royal-100'
    | 'royal-200'
    | 'royal-800'
    | 'royal-1000'
    | 'blue-700'
    | 'blue-800'
    | 'blue-900'
    | 'indigo-200';
  /** @todo add more colors */
  color?:
    | 'black'
    | 'gray-1000'
    | 'royal-600'
    | 'royal-800'
    | 'royal-1000'
    | 'white';
  opacity?: '10' | '20' | '30' | '40' | '50' | '60' | '70' | '80' | '90';
}

const boxColorToClassName: Record<
  keyof BoxColorProps,
  Record<string, string>
> = {
  bg: {
    white: 'bg-white',
    transparent: 'bg-transparent',
    'red-100': 'bg-red-100',
    'gray-100': 'bg-gray-100',
    'gray-200': 'bg-gray-200',
    'gray-300': 'bg-gray-300',
    'green-300': 'bg-green-300',
    'green-100': 'bg-green-100',
    'royal-100': 'bg-royal-100',
    'royal-200': 'bg-royal-200',
    'royal-800': 'bg-royal-800',
    'royal-1000': 'bg-royal-1000',
    'blue-700': 'bg-blue-700',
    'blue-800': 'bg-blue-800',
    'blue-900': 'bg-blue-900',
    'indigo-200': 'bg-indigo-200',
  },
  color: {
    black: 'text-black',
    'royal-600': 'text-royal-600',
    'royal-800': 'text-royal-800',
    'gray-1000': 'text-gray-1000',
    'royal-1000': 'text-royal-1000',
  },
  opacity: {
    '10': 'opacity-10',
    '20': 'opacity-20',
    '30': 'opacity-30',
    '40': 'opacity-40',
    '50': 'opacity-50',
    '60': 'opacity-60',
    '70': 'opacity-70',
    '80': 'opacity-80',
    '90': 'opacity-90',
  },
};

type Elevation =
  | 'border'
  | 'shadow-2px'
  | 'shadow-4px'
  | 'shadow-8px'
  | 'shadow-12px'
  | 'shadow-16px';

export interface ElevationProps {
  elevation?: Elevation;
}

type PointerEvents = 'none' | 'auto';

export interface PointerEventsProps {
  pointerEvents?: PointerEvents;
}

/**
 * @todo confirm with the design team if these names and values work for them
 * @url https://tailwindcss.com/docs/box-shadow
 */
const elevationToClassName: Record<Elevation, string> = {
  border: 'border border-1 border-gray-200',
  'shadow-2px': 'shadow-2px',
  'shadow-4px': 'shadow-4px',
  'shadow-8px': 'shadow-8px',
  'shadow-12px': 'shadow-12px',
  'shadow-16px': 'shadow-16px',
};

const pointerEventsToClassName: Record<PointerEvents, string> = {
  none: 'pointer-events-none',
  auto: 'pointer-events-auto',
};

const gapToClassName: Record<SpacingSize, string> = {
  xs: 'gap-2',
  sm: 'gap-4',
  md: 'gap-8',
  lg: 'gap-16',
  xl: 'gap-24',
};

export interface BoxFlexProps {
  display: 'flex';
  alignItems?: 'start' | 'center' | 'end' | 'baseline';
  justifyContent?: 'start' | 'center' | 'end' | 'between' | 'around';
  flexWrap?: 'noWrap' | 'wrap';
  flexDirection?: 'row' | 'col';
  flex?: '1' | '2';
  gap?: SpacingSize;
}

const alignItemsToClassName: Record<
  NonNullable<BoxFlexProps['alignItems']>,
  string
> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  baseline: 'items-baseline',
};

const justifyContentToClassName: Record<
  NonNullable<BoxFlexProps['justifyContent']>,
  string
> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

const alignSelfToClassName: Record<
  NonNullable<BoxLayoutProps['alignSelf']>,
  string
> = {
  start: 'self-start',
  center: 'self-center',
  end: 'self-end',
};

const flexDirectionToClassName: Record<
  NonNullable<BoxFlexProps['flexDirection']>,
  string
> = {
  row: 'flex-row',
  col: 'flex-col',
};

const flexWrapToClassName: Record<
  NonNullable<BoxFlexProps['flexWrap']>,
  string
> = {
  noWrap: 'flex-no-wrap',
  wrap: 'flex-wrap',
};

const flexToClassName: Record<NonNullable<BoxFlexProps['flex']>, string> = {
  '1': 'flex-1',
  '2': 'flex-2',
};

type GridColsRows =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | 'none'
  | 'subgrid'
  | 'auto'
  | 'auto-min'
  | 'auto-max'
  | 'auto-fr';

export interface BoxGridProps {
  display: 'grid';
  cols?: GridColsRows;
  rows?: GridColsRows;
  gap?: SpacingSize;
}

export interface BoxBlockProps {
  display: 'block';
}

export interface BoxInlineProps {
  display: 'inline';
}

export interface BoxInlineBlockProps {
  display: 'inline-block';
}

export interface BoxNoDisplayProps {
  display: 'none';
}

export interface BoxNeverDisplayProps {
  display?: never;
}

type BoxDisplayVariants =
  | 'flex'
  | 'grid'
  | 'block'
  | 'inline'
  | 'inline-block'
  | 'none';

export type BoxDisplayProps =
  | BoxFlexProps
  | BoxGridProps
  | BoxBlockProps
  | BoxInlineProps
  | BoxInlineBlockProps
  | BoxNoDisplayProps
  | BoxNeverDisplayProps;

const displayToClassName: Record<BoxDisplayVariants, string> = {
  flex: 'flex',
  grid: 'grid',
  block: 'block',
  inline: 'inline',
  'inline-block': 'inline-block',
  none: 'display-none',
};

export type AsElementProps = {
  as?:
    | 'div'
    | 'section'
    | 'article'
    | 'main'
    | 'header'
    | 'footer'
    | 'nav'
    | 'span'
    | 'p'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
};

export type BoxProps = BoxSpacingProps &
  BoxOverflowProps &
  BoxLayoutProps &
  BoxColorProps &
  BoxPositionProps &
  BoxBorderProps &
  ElevationProps &
  PointerEventsProps &
  BoxDisplayProps &
  AsElementProps & {
    children?: React.ReactNode;
    truncate?: boolean;
  };

// eslint-disable-next-line complexity
const styleToClassName = (style: string, value: string): string => {
  if (value === undefined) {
    return '';
  }

  if (isCustomSize(value)) {
    return value;
  }

  if (style in boxSizeToClassName) {
    return boxLayoutToClassName(style as keyof BoxLayoutProps, value) ?? '';
  }

  if (style in boxColorToClassName) {
    return boxColorToClassName[style as keyof BoxColorProps][value] ?? '';
  }

  if (style in boxPositionToClassNameMap) {
    return boxPositionToClassName(style as keyof BoxPositionProps, value) ?? '';
  }

  if (style === 'gap') {
    return gapToClassName[value as SpacingSize] ?? '';
  }

  if (style === 'alignItems') {
    return (
      alignItemsToClassName[value as keyof typeof alignItemsToClassName] ?? ''
    );
  }

  if (style === 'justifyContent') {
    return (
      justifyContentToClassName[
        value as keyof typeof justifyContentToClassName
      ] ?? ''
    );
  }

  if (style === 'alignSelf') {
    return (
      alignSelfToClassName[value as keyof typeof alignSelfToClassName] ?? ''
    );
  }

  if (style === 'flexDirection') {
    return (
      flexDirectionToClassName[
        value as keyof typeof flexDirectionToClassName
      ] ?? ''
    );
  }

  if (style === 'flexWrap') {
    return flexWrapToClassName[value as keyof typeof flexWrapToClassName] ?? '';
  }

  if (style === 'flex') {
    return flexToClassName[value as NonNullable<BoxFlexProps['flex']>] ?? '';
  }

  if (style === 'display') {
    return (
      displayToClassName[value as NonNullable<BoxDisplayProps['display']>] ?? ''
    );
  }

  if (style in boxSpacingToClassName) {
    return (
      boxSpacingToClassName[style as keyof typeof boxSpacingToClassName][
        value as SpacingSize
      ] ?? ''
    );
  }

  if (style in boxOverflowToClassName) {
    return (
      boxOverflowToClassName[style as keyof typeof boxOverflowToClassName][
        value as OverflowStyle
      ] ?? ''
    );
  }

  if (style in elevationToClassName) {
    return (
      elevationToClassName[style as keyof typeof elevationToClassName] ?? ''
    );
  }

  if (style === 'pointerEvents') {
    return (
      pointerEventsToClassName[
        value as keyof typeof pointerEventsToClassName
      ] ?? ''
    );
  }

  if (style in borderRadiusToClassName) {
    return (
      borderRadiusToClassName[style as keyof typeof borderRadiusToClassName][
        value as BorderRadiusSize
      ] ?? ''
    );
  }

  return '';
};

export const getClassNames = (props: BoxProps): string => {
  return cn(
    Object.entries(props).map(([prop, value]) => {
      if (typeof value !== 'string') {
        return '';
      }

      return styleToClassName(prop, value);
    }),
  );
};

// eslint-disable-next-line complexity
export const Box = forwardRef(function Box(
  props: BoxProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const className = getClassNames(props);

  const as = props.as ?? 'div';

  switch (as) {
    case 'div':
      return (
        <div ref={ref} className={className}>
          {props.children}
        </div>
      );
    case 'span':
      return (
        <span ref={ref} className={className}>
          {props.children}
        </span>
      );
    case 'section':
      return (
        <section ref={ref} className={className}>
          {props.children}
        </section>
      );
    case 'p':
      return (
        <p ref={ref} className={className}>
          {props.children}
        </p>
      );
    case 'h1':
      return (
        <h1 ref={ref} className={className}>
          {props.children}
        </h1>
      );
    case 'h2':
      return (
        <h2 ref={ref} className={className}>
          {props.children}
        </h2>
      );
    case 'h3':
      return (
        <h3 ref={ref} className={className}>
          {props.children}
        </h3>
      );
    case 'h4':
      return (
        <h4 ref={ref} className={className}>
          {props.children}
        </h4>
      );
    case 'h5':
      return (
        <h5 ref={ref} className={className}>
          {props.children}
        </h5>
      );
    case 'h6':
      return (
        <h6 ref={ref} className={className}>
          {props.children}
        </h6>
      );
    case 'article':
      return (
        <article ref={ref} className={className}>
          {props.children}
        </article>
      );
    case 'main':
      return (
        <main ref={ref} className={className}>
          {props.children}
        </main>
      );
    case 'header':
      return (
        <header ref={ref} className={className}>
          {props.children}
        </header>
      );
    case 'footer':
      return (
        <footer ref={ref} className={className}>
          {props.children}
        </footer>
      );
    case 'nav':
      return (
        <nav ref={ref} className={className}>
          {props.children}
        </nav>
      );
    default:
      return (
        <div ref={ref} className={className}>
          {props.children}
        </div>
      );
  }
});
